import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Live password-strength indicator.
 * Mirrors the backend rules in /api/auth/password-policy:
 *  - min 8 chars
 *  - 1 uppercase
 *  - 1 lowercase
 *  - 1 digit
 *  - 1 symbol from "@#$"
 *
 * Pass `onValidityChange` to lift the boolean validity into the parent
 * (used to disable the submit button until all rules pass).
 */
const ALLOWED_SYMBOLS = '@#$';

export function checkPasswordRules(password) {
  const p = password || '';
  return {
    length: p.length >= 8,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    digit: /\d/.test(p),
    symbol: new RegExp(`[${ALLOWED_SYMBOLS.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(p),
  };
}

export function isPasswordStrong(password) {
  const r = checkPasswordRules(password);
  return r.length && r.upper && r.lower && r.digit && r.symbol;
}

const RULES = [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'upper', label: '1 uppercase letter (A–Z)' },
  { key: 'lower', label: '1 lowercase letter (a–z)' },
  { key: 'digit', label: '1 digit (0–9)' },
  { key: 'symbol', label: `1 symbol (${ALLOWED_SYMBOLS})` },
];

export default function PasswordStrength({ password, onValidityChange }) {
  const rules = useMemo(() => checkPasswordRules(password), [password]);
  const passed = Object.values(rules).filter(Boolean).length;

  React.useEffect(() => {
    if (onValidityChange) onValidityChange(passed === RULES.length);
  }, [passed, onValidityChange]);

  const barColor =
    passed <= 2 ? 'bg-red-500'
    : passed <= 3 ? 'bg-amber-500'
    : passed <= 4 ? 'bg-yellow-500'
    : 'bg-emerald-500';

  return (
    <div className="space-y-2 mt-2" data-testid="password-strength">
      <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${barColor}`}
          style={{ width: `${(passed / RULES.length) * 100}%` }}
          data-testid="password-strength-bar"
        />
      </div>
      <ul className="space-y-1">
        {RULES.map((r) => (
          <li
            key={r.key}
            className={`flex items-center gap-2 text-xs ${rules[r.key] ? 'text-emerald-600' : 'text-slate-500'}`}
            data-testid={`pw-rule-${r.key}`}
          >
            {rules[r.key]
              ? <Check className="w-3.5 h-3.5" />
              : <X className="w-3.5 h-3.5" />}
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
